import { AnalyticsEnvironment } from './amplitude-api-key';

/**
 * Пути прокси Amplitude на домене админки — `location` в `nginx/admin.*.conf`. Меняются только вместе с конфигами.
 * Цели прокси — по документации Amplitude:
 * [Domain Proxy](https://amplitude.com/docs/analytics/domain-proxy),
 * [Session Replay plugin](https://amplitude.com/docs/session-replay/session-replay-plugin).
 */
export const AMPLITUDE_PROXY_PATHS = {
  /** → `https://api2.amplitude.com/2/httpapi` */
  events: '/amp/2/httpapi',
  /** → `https://api-sr.amplitude.com/sessions/v2/track` */
  replayTrack: '/amp/sr/sessions/v2/track',
  /** → `https://sr-client-cfg.amplitude.com/config` */
  replayConfig: '/amp/sr-config/config',
} as const;

export interface AmplitudeProxyUrls {
  serverUrl: string;
  trackServerUrl: string;
  configServerUrl: string;
}

/** Помним, что прямой запрос к Amplitude в этом браузере отрезан: со следующей загрузки шлём через наш домен. */
export const AMPLITUDE_BLOCKED_STORAGE_KEY = 'imgame:analytics:amplitudeBlocked';

/**
 * Адреса прокси для этой загрузки или `null` — слать напрямую.
 *
 * Через прокси идут **только** браузеры, где прямой запрос уже был отрезан (блокировщик): Amplitude определяет
 * страну и город по IP запроса, а через nginx он увидит IP нашего сервера — геолокация пропала бы у всех. Локально
 * прокси нет вовсе: на дев-сервере нет nginx.
 */
export function resolveAmplitudeProxyUrls(environment: AnalyticsEnvironment, origin: string): AmplitudeProxyUrls | null {
  if (environment === 'local' || !readBlocked()) {
    return null;
  }

  return {
    serverUrl: `${origin}${AMPLITUDE_PROXY_PATHS.events}`,
    trackServerUrl: `${origin}${AMPLITUDE_PROXY_PATHS.replayTrack}`,
    configServerUrl: `${origin}${AMPLITUDE_PROXY_PATHS.replayConfig}`,
  };
}

export function rememberAmplitudeBlocked(blocked: boolean): void {
  try {
    if (blocked) {
      window.localStorage.setItem(AMPLITUDE_BLOCKED_STORAGE_KEY, '1');
    } else {
      window.localStorage.removeItem(AMPLITUDE_BLOCKED_STORAGE_KEY);
    }
  } catch {
    // Хранилище недоступно (приватный режим) — останемся на прямом пути, как и без него.
  }
}

function readBlocked(): boolean {
  try {
    return window.localStorage.getItem(AMPLITUDE_BLOCKED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}
